import {
  PartnerIntegrations,
  ShowNotificationFunctionProps,
} from 'lib-integrations-partner-widgets';
import { useHasAccess } from 'lib-shared-access-control';
import { config } from 'lib-shared-backend-config';
import {
  MetaConversation,
  NotificationPayloadT,
} from 'lib-shared-legacy-types';
import { useCallback, useEffect, useState } from 'react';

import { notificationActions } from 'components/notifications/actions/notification';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { usePartnerWidgetFeatureFlags } from 'utils/partnerIntegrationsWidget/usePartnerWidgetFeatureFlags';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useServerStorage } from 'utils/useServerStorage';

import { CustomCards } from '../../cards/customCards/customCards';
import type { SidebarCardId } from '../../conversationSidebar.types';
import { useCardVisibility } from '../../hooks/useCardVisibility';
import { useSidebarMode } from '../../hooks/useSidebarMode';
import { partnerIntegrationNotificationTimeout } from '../../partnerIntegrations';
import { reorderNumeric } from '../../utils/reorder';
import { CardMoveHandle } from '../cardMoveHandle/cardMoveHandle';
import { CardSection } from '../cardSection/cardSection';
import { VisibilityToggle } from '../visibilityToggle/visibilityToggle';

type IntegrationCardsProps = {
  metaConversation: MetaConversation;
  isMinimized: boolean;
  isCardOpen: (card: string) => boolean;
  toggleCard: (card: string) => void;
  index: number;
};

const type = Symbol('nativeIntegrationCard');

export const IntegrationCards = ({
  metaConversation,
  isMinimized,
  isCardOpen,
  toggleCard,
  index,
}: IntegrationCardsProps): JSX.Element | null => {
  const [hiddenCardsSnapshot, setHiddenCardsSnapshot] = useState(
    new Set<SidebarCardId>(),
  );
  const { hiddenCards } = useCardVisibility();
  const sidebarMode = useSidebarMode();

  const dispatch = useAppDispatch();
  const flags = useFeatureFlag([
    'partner-integrations-widget',
    'conversation-right-sidebar-visibility',
  ]);

  const hasIntegrationAccess = useHasAccess('conversation.integration');

  const partnerWidgetFeatureFlags = usePartnerWidgetFeatureFlags();

  const showNotification = useCallback(
    ({ title, text, icon, severity }: ShowNotificationFunctionProps) => {
      const time =
        severity === 'warning' || severity === 'danger'
          ? 0
          : partnerIntegrationNotificationTimeout;
      const className = severity as string;

      const payload: NotificationPayloadT = {
        title,
        text,
        icon,
        className,
        time,
      };
      dispatch(notificationActions.notificationAdd(payload));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const storage = useServerStorage('user');

  const handleMove = useCallback(
    (
      { dragIndex, hoverIndex }: { dragIndex: number; hoverIndex: number },
      items: { id: string }[],
    ) => {
      storage.set({
        conversation: (draft) => {
          draft.sidebarIntegrationOrder = reorderNumeric(
            items,
            dragIndex,
            hoverIndex,
          );
        },
      });
    },
    [storage],
  );

  const getVisibilityProperties = useCallback(
    (key: string) => ({
      isHidden: sidebarMode !== 'visibility' && hiddenCards.has(key),
      toggler: <VisibilityToggle cardId={key} />,
    }),
    [sidebarMode, hiddenCards],
  );

  const sortFn = useCallback(
    (
      a: { id: string; name: string },
      b: { id: string; name: string },
      keyA: string,
      keyB: string,
    ) => {
      const cardOrder =
        storage.data.conversation?.sidebarIntegrationOrder ?? {};

      return (
        (sidebarMode === 'visibility'
          ? Number(hiddenCardsSnapshot.has(keyB)) -
            Number(hiddenCardsSnapshot.has(keyA))
          : 0) ||
        (cardOrder[a.id] ?? -Infinity) - (cardOrder[b.id] ?? -Infinity) ||
        a.name.localeCompare(b.name)
      );
    },
    [
      sidebarMode,
      hiddenCardsSnapshot,
      storage.data.conversation?.sidebarIntegrationOrder,
    ],
  );

  useEffect(() => {
    if (sidebarMode === 'visibility') {
      setHiddenCardsSnapshot(hiddenCards);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarMode]);

  if (!hasIntegrationAccess) {
    return null;
  }

  const title = flags.conversationRightSidebarVisibility
    ? 'Native integrations'
    : 'Integrations';

  return (
    <CardSection
      title={!isMinimized ? title : undefined}
      index={index}
      isMinimized={isMinimized}
    >
      {flags.partnerIntegrationsWidget && (
        <PartnerIntegrations
          configuration={{
            baseUrl: config.url.partnerIntegrations,
            featureFlags: partnerWidgetFeatureFlags,
          }}
          conversationContext={{
            conversationId: metaConversation.conversation?.id || null,
            agent:
              metaConversation?.conversation?.assignment?.agent ?? undefined,
            customer: metaConversation?.requester ?? undefined,
          }}
          showNotification={showNotification}
          onToggleFn={toggleCard}
          isOpenFn={isCardOpen}
          isMinimized={isMinimized}
          renderDragElement={(items, idx, row) => (
            <CardMoveHandle
              index={idx}
              type={type}
              target={row}
              onMove={(event) => handleMove(event, items)}
            />
          )}
          getVisibilityProperties={getVisibilityProperties}
          sortFn={sortFn}
        />
      )}

      {!flags.conversationRightSidebarVisibility && (
        <CustomCards
          isMinimized={isMinimized}
          csid={metaConversation.conversation?.id}
        />
      )}
    </CardSection>
  );
};
